import React, { useEffect, useState } from "react";
import { Table, Card, Button, Input, Space, Row, Col, Typography, message, Modal, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import axiosInstance from "../../../configs";
import { canDelete, MODULES } from "../../../utils/rbac";

const { Title, Text } = Typography;
const { Search } = Input;

export default function UserList() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [search, setSearch] = useState("");
    const [userLevels, setUserLevels] = useState({});

    // Fetch user levels from database
    useEffect(() => {
        const fetchUserLevels = async () => {
            try {
                const res = await axiosInstance.get("/roles");
                const levelsMap = {};
                res.data.forEach(role => {
                    levelsMap[role.id_user_level] = {
                        label: role.user_level,
                        color: getColorByLevel(role.id_user_level)
                    };
                });
                setUserLevels(levelsMap);
            } catch (err) {
                console.error("Failed to fetch user levels:", err);
            }
        };
        fetchUserLevels();
    }, []);

    const getColorByLevel = (level) => {
        const colors = ["red", "blue", "green", "purple", "orange", "cyan", "gold", "magenta"];
        return colors[(level - 1) % colors.length] || "default";
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get("/user", {
                params: { search }
            });
            setData(res.data || []);
        } catch (err) {
            console.error(err);
            message.error("Gagal mengambil data user");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [search]);

    const handleDelete = (id, username) => {
        Modal.confirm({
            title: "Hapus User?",
            content: `User "${username}" akan dihapus. Data yang dihapus tidak dapat dikembalikan.`,
            okText: "Ya, Hapus",
            okType: "danger",
            onOk: async () => {
                try {
                    await axiosInstance.delete(`/user/${id}`);
                    message.success("User berhasil dihapus");
                    fetchData();
                } catch (e) {
                    message.error(e.response?.data?.error || "Gagal menghapus user");
                }
            }
        });
    };

    const columns = [
        {
            title: "Username",
            dataIndex: "username",
            key: "username",
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: "Nama",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Level",
            dataIndex: "id_user_level",
            key: "id_user_level",
            width: 120,
            render: (val) => {
                const level = userLevels[val] || { label: `Level ${val}`, color: "default" };
                return <Tag color={level.color}>{level.label}</Tag>;
            }
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (val) => (
                val === 1
                    ? <Tag color="green">Aktif</Tag>
                    : <Tag color="default">Non-Aktif</Tag>
            )
        },
        {
            title: "Aksi",
            key: "action",
            width: 120,
            align: "center",
            render: (_, r) => (
                <Space>
                    <Link to={`/referensi/users/edit/${r.id_user}`}>
                        <Button icon={<EditOutlined />} size="small" type="primary" />
                    </Link>
                    {canDelete(MODULES.USER) && (
                        <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                            onClick={() => handleDelete(r.id_user, r.username)}
                        />
                    )}
                </Space>
            )
        }
    ];

    return (
        <Card>
            <Row justify="space-between" style={{ marginBottom: 20 }}>
                <Col>
                    <Title level={4}>Daftar User</Title>
                </Col>
                <Col>
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={fetchData}>Refresh</Button>
                        <Link to="/referensi/users/create">
                            <Button type="primary" icon={<PlusOutlined />}>Tambah Baru</Button>
                        </Link>
                    </Space>
                </Col>
            </Row>

            <Row style={{ marginBottom: 16 }}>
                <Col span={8}>
                    <Search
                        placeholder="Cari username atau nama..."
                        onSearch={val => setSearch(val)}
                        allowClear
                        enterButton
                    />
                </Col>
            </Row>

            <Table
                dataSource={data}
                columns={columns}
                rowKey="id_user"
                loading={loading}
                scroll={{ x: 'max-content' }}
                pagination={{ pageSize: 20 }}
            />
        </Card>
    );
}
